import { AppButton, AppFilePicker, AppMultiInputText, AppRadioBox, AppTextField } from 'components';
import Editor from 'components/CKEditor/Editor';
import VideosDropDown from 'components/molecules/VideosDropDown';
import useManageTopicVideos from 'hooks/API/useManageTopicVideos';
import { UserContext } from 'context/UserContext';
import { Fragment, useContext, useEffect, useState } from 'react';
import { AiOutlineCloudUpload, AiOutlinePlusCircle } from 'react-icons/ai';
import { useParams } from 'react-router-dom';
import { checkForErrors } from 'utilities/fns';
import useToaster from 'hooks/useToaster';
import useTopicVideos from 'hooks/API/POST/useTopicVideos';
import Joi from 'joi';
import DOMPurify from 'dompurify';
import VideoCommentDropBox from '../../PreviewSection/VideoCommentDropBox';
import ReviewContent from '../ReviewContent';

function ManageVideos({ data, editMode, toggle, subtopicID }) {
  const { topicID } = useParams();
  const [videoSection, setVideoSection] = useState('local');
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('draft');

  const [videoID, setVideoID] = useState('');
  const [videoFiles, setVideoFiles] = useState([]);

  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [videoTags, setVideoTags] = useState([]);
  const [formErrors, setFormErrors] = useState([]);

  const [commentsModal, setCommentsModal] = useState(Object.assign({}, _initialVideoState));
  const [replyModal, setReplyModal] = useState({ open: false, apiHash: null });
  const { userPermissions = {}, userInfo = {} } = useContext(UserContext);
  const errors = checkForErrors(formErrors);

  useEffect(() => {
    if (editMode?.videoTags)
      setVideoTags(editMode?.videoTags?.map?.((x) => ({ label: x, value: x })));
    if (editMode?.name) setVideoTitle(editMode?.name);
    if (editMode?.description) setVideoDescription(DOMPurify.sanitize(editMode?.description));
    if (editMode?.video?.thumbnail) setFile({ meta: { file: editMode.video.thumbnail } });
    if (editMode?.status) setStatus(editMode?.status);
  }, [editMode]);

  const {
    mutate: saveLocalVideos,
    isLoading: isVideoUploading,
    isError: videoError,
  } = useManageTopicVideos({ topicID });
  const { mutate, isLoading, isError } = useTopicVideos({ topicID });

  function toasterConfig(tabValue) {
    if (tabValue === 'local') return [isVideoUploading, { type: videoError ? 'error' : 'success' }];
    if (tabValue === 'library') return [isLoading, { type: isError ? 'error' : 'success' }];
    return [];
  }

  useToaster(...toasterConfig(videoSection));

  function handleSaveDetails() {
    const apiData = {
      name: videoTitle,
      description: DOMPurify.sanitize(videoDescription),
      videoTags: videoTags?.map?.((x) => x.value),
      thumbnail: file?.meta?.file,
    };
    if (editMode?._id) apiData.status = status;
    const _data = { videos: [{ ...editMode, ...apiData }], subtopicID };

    const validation = tab1VideoSchema.validate(apiData, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    saveLocalVideos(_data);
    clear();
  }

  function handleSaveLocalVideos() {
    if (isVideoUploading) return;
    if (videoFiles.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < videoFiles.length; i += 1) {
      formData.append('file', videoFiles[i]);
    }

    saveLocalVideos({
      subtopicID,
      data: formData,
      headers: {
        'X-access-control': 'private',
        'X-reference-key': 'VIDEOS',
      },
      type: 'UPLOAD_VIDEOS',
    });

    setVideoFiles([]);
  }

  function handleVideoLink(args = {}) {
    if (isLoading) return;

    setFormErrors([]);

    const apiData = {
      videoId: videoID,
      topic: topicID,
      ...{
        class: data?.class,
        chapter: data?.chapter?._id,
        subject: data?.subject,
      },
      ...args,
      videoTitle,
      videoDescription,
      videoTags: videoTags?.map((x) => x.value),
    };

    const validation = tab3VideoSchema.validate(apiData, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    mutate({ data: apiData });
    setVideoID('');
  }

  function clear() {
    setVideoTitle('');
    setVideoDescription('');
    setVideoTags([]);

    toggle();
  }

  return (
    <>
      {(replyModal.open || commentsModal.open) && (
        <VideoCommentDropBox
          open={replyModal.open || commentsModal.open}
          close={() => {
            setCommentsModal(Object.assign({}, _initialVideoState));
            setReplyModal({ open: false, apiHash: null });
          }}
          data={{
            currentTimestamp: commentsModal?.data?.timestamp,
            replyModal,
            apiBodyProps: {
              topic: editMode?.topic,
              subtopic: subtopicID,
              chapter: editMode?.chapter,
              class: editMode?.class,
              video: editMode?._id,
            },
          }}
        />
      )}
      <div className="row">
        <div className="col-12">
          {editMode?.originalFileName && (
            <h2 className="prime-head p-0 boldtitle">
              File selected: {editMode?.originalFileName}
            </h2>
          )}
          {!editMode?.originalFileName && <h2 className="prime-head p-0 boldtitle">Add videos</h2>}
          <span className="boldtitle mt-5 conaddtitle">Add details</span>
        </div>
      </div>
      <div className="row">
        {editMode !== null && (
          <>
            <div className="col-lg-5 col-md-6 col-sm-12">
              <div className="w-100 mt-4">
                <AppTextField
                  inputProps={{
                    value: videoTitle,
                    onChange: (event) => setVideoTitle(event.target.value),
                    placeholder: 'Video Title *',
                  }}
                  labelProps={{ label: 'Video Title *' }}
                  fieldErrors={errors['videoTitle']}
                />
              </div>
              <div className="w-100 mt-4">
                <label htmlFor="">Video Description *</label>
                <Editor
                  data={{
                    value: videoDescription,
                    onChange: (data) => setVideoDescription(data ?? ''),
                  }}
                />
              </div>
              <div className="w-100 mt-4 ">
                <AppMultiInputText
                  heading={'Video Search Tags'}
                  value={videoTags}
                  setValue={(data) => setVideoTags(data)}
                />
              </div>
            </div>
            <div className="col-lg-5 col-md-6 col-sm-12">
              <div className="w-100 mt-4">
                <label htmlFor="">Choose thumbnail image (.PNG Format)</label>
                <div>
                  <AppFilePicker
                    text="Upload PNG"
                    handleChange={(data) => setFile(data)}
                    inputProps={{ accept: '.png' }}
                  />
                </div>
              </div>

              <div className="w-100 mt-4">
                <AppButton
                  variantClass="btn-plane d-flex align-items-center"
                  onClick={() => {
                    let data = {};
                    const _videoTag = document.getElementById(`app-vi-${editMode?._id}`);
                    if (_videoTag) data = { timestamp: _videoTag.currentTime };
                    setCommentsModal({
                      open: true,
                      data,
                    });
                  }}
                >
                  <AiOutlinePlusCircle className="mx-2" />
                  Add Comment
                </AppButton>
              </div>
              {editMode?.videoReviews?.length > 0 && (
                <div
                  className="w-100 mt-4"
                  style={{ maxHeight: '200px', height: '170px', overflow: 'scroll' }}
                >
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">#</th>
                        <th scope="col">Timestamp (min : sec)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editMode?.videoReviews?.map((review, index) => {
                        return (
                          <tr key={review?._id}>
                            <th scope="row">{index + 1}</th>
                            <td
                              onClick={() => {
                                if (review?.resolved) return;

                                setReplyModal({ open: true, apiHash: review?._id });
                              }}
                              className="displayPointer"
                            >
                              {`0${review?.timestamp?.inMin || 0}`.slice(-2)}:{' '}
                              {`0${review?.timestamp?.inSec || 0}`.slice(-2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {editMode && (
                <div className="w-100 mt-4">
                  <ReviewContent
                    area={userPermissions?.topic?.notes}
                    value={status}
                    onChange={(value) => setStatus(value)}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {!editMode?._id && !editMode?.video?.link && (
          <div className="col-lg-5 col-md-6  col-sm-12 videochoosefrom ">
            <div className="w-100 mt-4">
              <AppRadioBox
                inputProps={{
                  name: 'videoTab',
                  checked: videoSection === 'library',
                  onChange: () => setVideoSection('library'),
                }}
                labelProps={{ label: 'Choose from library' }}
              />
              {videoSection === 'library' && (
                <VideosDropDown
                  enableAPI
                  value={videoID}
                  handleChange={(event) => setVideoID(event.target.value)}
                  label=""
                  fieldErrors={errors['videoId']}
                />
              )}
            </div>
            <div className="w-100 mt-5 tab-nmp">
              <AppRadioBox
                inputProps={{
                  name: 'videoTab',
                  checked: videoSection === 'local',
                  onChange: () => setVideoSection('local'),
                }}
                labelProps={{ label: 'Choose from this Local Machine' }}
              />
              {videoSection === 'local' && (
                <>
                  <div>
                    <label htmlFor="Video#upload#all#files" className="btn-right-content upload">
                      <span className="mx-2">
                        <AiOutlineCloudUpload />
                      </span>
                      <span>Upload video file</span>
                    </label>
                    <input
                      multiple
                      type="file"
                      id="Video#upload#all#files"
                      style={{ display: 'none' }}
                      accept=".mp4"
                      onChange={(event) => setVideoFiles(event.target.files)}
                    />
                  </div>
                  <div className="row">
                    {Array.from(videoFiles)?.map?.((file) => (
                      <span key={file.name} className="badge badge-primary mx-3">
                        {file.name}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="row mt-5 btnsubmitmain">
        <div className="col-12 btnTab-foot">
          <button
            type="button"
            className="btn-right-content save"
            onClick={() => {
              if (editMode?._id) handleSaveDetails();
              if (!editMode?._id && videoSection === 'local') handleSaveLocalVideos();
              if (!editMode?._id && videoSection === 'library')
                handleVideoLink({ type: 'video', name: 'video' });
            }}
          >
            Save Changes
          </button>
          {editMode?._id && (
            <AppButton variantClass="btn-plane p--tint mx-4" onClick={clear}>
              Clear
            </AppButton>
          )}
        </div>
      </div>
    </>
  );
}

const tab3VideoSchema = Joi.object({
  videoId: Joi.string().required().trim(),
});

const tab1VideoSchema = Joi.object({
  //name: Joi.string().required().trim(),
  //description: Joi.string().required().trim(),
});

const _initialVideoState = {
  open: false,
  data: {},
};

export default ManageVideos;
